/**
 * Per-endpoint rate-limit cooldowns.
 *
 * Ported from WalletFront's `client.js` — this part is genuine production
 * hardening and worth keeping. Once an endpoint answers 429 with a Retry-After,
 * every further call to that same METHOD+path is rejected locally until the
 * window elapses, instead of hammering a backend that is already refusing.
 * Novix shares a 120 req/min budget with the partner API, so client-side
 * restraint is not optional.
 */

const MAX_COOLDOWN_SECONDS = 60 * 60;

const cooldowns = new Map<string, number>();

export function requestKeyOf(method: string, path: string): string {
  return `${method.toUpperCase()} ${path}`;
}

/** Clamps anything to a sane, finite, non-negative whole number of seconds. */
export function normalizeSeconds(value: unknown): number {
  const numeric =
    typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;
  if (!Number.isFinite(numeric) || numeric <= 0) return 0;
  return Math.min(Math.ceil(numeric), MAX_COOLDOWN_SECONDS);
}

/** Reads Retry-After (delta-seconds or HTTP-date) plus common JSON fallbacks. */
export function extractRetryAfterSeconds(
  headers: Headers | null | undefined,
  body?: unknown
): number {
  const headerValue = headers?.get('Retry-After') ?? headers?.get('retry-after');

  if (headerValue) {
    const asSeconds = normalizeSeconds(headerValue);
    if (asSeconds > 0) return asSeconds;

    const asDate = Date.parse(headerValue);
    if (!Number.isNaN(asDate)) {
      return normalizeSeconds((asDate - Date.now()) / 1000);
    }
  }

  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    for (const key of ['retryAfterSeconds', 'retryAfter', 'retry_after']) {
      const candidate = normalizeSeconds(record[key]);
      if (candidate > 0) return candidate;
    }
  }

  return 0;
}

export function setCooldown(requestKey: string, retryAfterSeconds: number): void {
  const seconds = normalizeSeconds(retryAfterSeconds);
  if (!requestKey || seconds <= 0) return;
  cooldowns.set(requestKey, Date.now() + seconds * 1000);
}

/** Seconds still to wait for this endpoint, or 0 if it is free. */
export function getCooldownSeconds(requestKey: string): number {
  const blockedUntil = cooldowns.get(requestKey);
  if (blockedUntil === undefined) return 0;

  const secondsLeft = Math.ceil((blockedUntil - Date.now()) / 1000);
  if (secondsLeft <= 0) {
    cooldowns.delete(requestKey);
    return 0;
  }
  return secondsLeft;
}

export function clearCooldowns(): void {
  cooldowns.clear();
}
